import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import type { CClassesKey, ILightingClassConfig, ISdFpath, PClassesKey } from "types";
import { roundToTwo } from "utils/helpers";
import { getBorderAreaTargetValues } from "../../utils/border-class";
import { useIlluminanceData } from "../../hooks/useIlluminanceData";
import type { Illuminance, SidewalkStateItem, SurfaceWithIlluminance } from "../../types";
import IlluminanceBordersHeatmap from "../heatmaps/IlluminanceBordersHeatmap";
import getStyles from "./styles";

type IlluminanceBordersProps = {
  illuminance: Illuminance;
  surfaces: SurfaceWithIlluminance[];
  sidewalk_placing: string | null;
  subVal?: ISdFpath[];
  subValResult: SidewalkStateItem[];
  lightingClassConfig: ILightingClassConfig;
  divId?: string;
  ulor?: number;
  f_ti?: number;
};

const IlluminanceBorders: FC<IlluminanceBordersProps> = ({
  illuminance,
  surfaces,
  sidewalk_placing,
  subVal,
  subValResult,
  lightingClassConfig,
  divId,
  ulor,
  f_ti,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).IlluminanceBorders, [theme]);

  const { ptsX, ptsY, dimX, dimY, leftSideWalk, rightSideWalk } = useIlluminanceData({ illuminance, surfaces, sidewalk_placing, subVal, subValResult });
  const roadClass = lightingClassConfig.roadClasses;
  const customRoadClass = lightingClassConfig.customClasses?.mainCustomClass;
  const customRightBorderClass = lightingClassConfig.customClasses?.rightBorderCustomClass;
  const customLeftBorderClass = lightingClassConfig.customClasses?.leftBorderCustomClass;
  const borderAreaLeftClass = lightingClassConfig.borderAreaLeftClasses as CClassesKey | PClassesKey;
  const borderAreaRightClass = lightingClassConfig.borderAreaRightClasses as CClassesKey | PClassesKey;

  const leftBorderTarget = getBorderAreaTargetValues(borderAreaLeftClass, customLeftBorderClass);
  const rightBorderTarget = getBorderAreaTargetValues(borderAreaRightClass, customRightBorderClass);

  const isPClass = customRoadClass?.class_type?.includes("P") ?? roadClass.includes("P");
  const ulorDisplay = (ulor || ulor === 0) && !Number.isNaN(Number(ulor)) ? `${Math.round(Number(ulor))}%` : "—";
  const ftiDisplay = f_ti && Number(f_ti) ? roundToTwo(Number(f_ti)) : "—";

  return (
    <Stack sx={styles.container}>
      {Array.isArray(subVal) && subVal.length > 0 && (
        <Box>
          <Typography variant="h2" sx={styles.title}>
            {t("Illuminance calculation field:")} <span id="dimX1">{dimX}</span> m &times; <span id="dimY1">{roundToTwo(dimY)}</span> m (
            <span id="ptsX1">{ptsX}</span> &times; <span id="ptsY1">{ptsY}</span> {t("pts")})
          </Typography>

          <TableContainer component={Paper} sx={styles.card}>
            <Table padding="none" sx={styles.table}>
              <TableHead sx={styles.thead}>
                <TableRow>
                  <TableCell>{t("Road")}</TableCell>
                  <TableCell>
                    Ē<sub>m</sub>
                  </TableCell>
                  <TableCell>
                    E<sub>min</sub>
                  </TableCell>
                  <TableCell>
                    U<sub>o</sub>
                    {isPClass && <span> *</span>}
                  </TableCell>
                  <TableCell>
                    U<sub>d</sub>
                    {isPClass && <span> *</span>}
                  </TableCell>
                  <TableCell>
                    f<sub>TI</sub>
                  </TableCell>
                  <TableCell>
                    R<sub>UL</sub>
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* Left Border Area */}
              {leftSideWalk.name === "sd-fpath-left" && (
                <TableBody sx={styles.tbody}>
                  <TableRow>
                    <TableCell>{t("Border Area Left")}</TableCell>
                    <TableCell>{leftSideWalk.e_avg?.toFixed(2) || ""}</TableCell>
                    <TableCell>{leftSideWalk.e_min?.toFixed(2) || ""}</TableCell>
                    <TableCell>{leftSideWalk.u_oe?.toFixed(2) || ""}</TableCell>
                    <TableCell>{leftSideWalk.u_de?.toFixed(2) || ""}</TableCell>
                    <TableCell>{ftiDisplay}</TableCell>
                    <TableCell>{ulorDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("Target Value")}{" "}
                      {customLeftBorderClass?.class_type ? `(${customLeftBorderClass.class_type} ${t("Customized")})` : borderAreaLeftClass}:
                    </TableCell>
                    <TableCell>{leftBorderTarget.eavTargetValue || ""} lx</TableCell>
                    <TableCell>{leftBorderTarget.eminTargetValue || ""}</TableCell>
                    <TableCell>{leftBorderTarget.uoTargetValue || ""}</TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableBody>
              )}

              {/* Right Border Area */}
              {rightSideWalk.name === "sd-fpath-right" && (
                <TableBody sx={styles.tbody}>
                  <TableRow>
                    <TableCell>{t("Border Area Right")}</TableCell>
                    <TableCell>{rightSideWalk.e_avg?.toFixed(2) || ""}</TableCell>
                    <TableCell>{rightSideWalk.e_min?.toFixed(2) || ""}</TableCell>
                    <TableCell>{rightSideWalk.u_oe?.toFixed(2) || ""}</TableCell>
                    <TableCell>{rightSideWalk.u_de?.toFixed(2) || ""}</TableCell>
                    <TableCell>{ftiDisplay}</TableCell>
                    <TableCell>{ulorDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("Target Value")}{" "}
                      {customRightBorderClass?.class_type ? `(${customRightBorderClass.class_type} ${t("Customized")})` : borderAreaRightClass}:
                    </TableCell>
                    <TableCell>{rightBorderTarget.eavTargetValue || ""} lx</TableCell>
                    <TableCell>{rightBorderTarget.eminTargetValue || ""}</TableCell>
                    <TableCell>{rightBorderTarget.uoTargetValue || ""}</TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableBody>
              )}

              {/* Informational Note */}
              {isPClass && (
                <Typography variant="body2" component="caption" sx={styles.caption}>
                  * {t("In M and C classes Uo is normative. Ud is in all classes informative.")}
                </Typography>
              )}
            </Table>
          </TableContainer>
        </Box>
      )}

      <IlluminanceBordersHeatmap
        id={divId ? `${divId}-heatmaproot-left-border` : "heatmaproot-left-border"}
        illuminance={illuminance}
        surfaces={surfaces}
        sidewalkPlacing={sidewalk_placing}
        subVal={subVal}
        subValResult={subValResult}
      />
    </Stack>
  );
};

export default IlluminanceBorders;
